import {
  ModelGroup,
  DocumentGroup,
  ModelBaseGroup,
  DocumentBaseGroup,
  ModelArrayGroup,
  DocumentArrayGroup,
  ModelGroupModalButton,
  DocumentGroupModalButton,
} from '@/engine';
import {computeField} from './document-field';
import {DocumentProcessorUtils} from './utils';

export const computeGroupModalButton =
  (utils: DocumentProcessorUtils) =>
  ({label, theme, trackingKey, onClick}: ModelGroupModalButton): DocumentGroupModalButton => {
    const {computeEvents, valueResolver, visibilitySet} = utils;

    return {
      label: label(valueResolver, visibilitySet),
      theme,
      trackingKey,
      onClick: computeEvents(onClick),
    };
  };

export const computeDocumentBaseGroup =
  (utils: DocumentProcessorUtils, sectionId: string) =>
  ({
    title,
    displayIf,
    fields,
    onInit,
    onMount,
    onSave,
    onCancel,
    onDelete,
    buttons,
    ...group
  }: ModelBaseGroup): DocumentBaseGroup => {
    const {computeConditions, computeEvents, valueResolver, visibilitySet} = utils;

    const isSectionVisible = visibilitySet.has(sectionId);
    const visible = isSectionVisible ? computeConditions(displayIf) : false;
    if (visible) {
      visibilitySet.add(group.id);
    } else {
      visibilitySet.delete(group.id);
    }

    return {
      ...group,
      title: title(valueResolver, visibilitySet),
      onInit: computeEvents(onInit),
      onMount: computeEvents(onMount),
      onSave: computeEvents(onSave),
      onCancel: computeEvents(onCancel),
      onDelete: computeEvents(onDelete),
      buttons: buttons.map(computeGroupModalButton(utils)),
      visible,
      fields: fields.map(computeField(utils, group.id)),
    };
  };

export const computeArrayGroup =
  (utils: DocumentProcessorUtils, sectionId: string) =>
  ({id, type, rows}: ModelArrayGroup): DocumentArrayGroup => {
    const {valueResolver, visibilitySet} = utils;
    return {
      id,
      type,
      rows: rows(valueResolver, visibilitySet).map(computeDocumentBaseGroup(utils, sectionId)),
    };
  };

export const computeGroup =
  (utils: DocumentProcessorUtils, sectionId: string) =>
  (group: ModelGroup): DocumentGroup => {
    if ('type' in group) {
      switch (group.type) {
        case 'array':
          return computeArrayGroup(utils, sectionId)(group);
        default:
          throw new Error(`Unsupported group type: ${group.type}`);
      }
    }

    return computeDocumentBaseGroup(utils, sectionId)(group);
  };
